import { useEffect, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { firestore } from "../../firebaseConfig";

const MAX_ADDRESSES = 10;
const PER_QUERY = 30;

const toMs = (d) => d.timestamp?.toMillis?.() || Date.parse(d.date || "") || d.sentAt || null;
const stripHtml = (s) => String(s || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

/**
 * Every mail we sent to — and received from — the known addresses of a
 * contact's company, newest first. Team members without inbox access simply
 * get an empty list (the reads are rule-protected).
 */
export default function useContactMailHistory(worldId, contactId, addresses) {
  const key = [...new Set((addresses || []).map(a => a.toLowerCase().trim()).filter(Boolean))].sort().slice(0, MAX_ADDRESSES).join(",");
  const requestKey = worldId && (contactId || key) ? `${worldId}\n${contactId || ""}\n${key}` : "";
  const [snapshot, setSnapshot] = useState({ requestKey: "", mails: [] });

  useEffect(() => {
    if (!requestKey) {
      setSnapshot(prev => (prev.requestKey === "" && prev.mails.length === 0 ? prev : { requestKey: "", mails: [] }));
      return undefined;
    }
    let cancelled = false;
    const list = key ? key.split(",") : [];
    const sent = collection(firestore, "world_sent", worldId, "emails");
    const inbox = collection(firestore, "world_inbox", worldId, "emails");
    const run = (q) => getDocs(q).then(s => s.docs).catch(() => []);

    Promise.all([
      contactId ? run(query(sent, where("leadId", "==", contactId), limit(PER_QUERY))) : [],
      list.length ? run(query(sent, where("to", "in", list), limit(PER_QUERY))) : [],
      list.length ? run(query(inbox, where("fromEmail", "in", list), limit(PER_QUERY))) : [],
    ]).then(([byLead, byTo, received]) => {
      if (cancelled) return;
      const out = new Map();
      [...byLead, ...byTo].forEach(d => {
        const m = d.data();
        out.set(`s_${d.id}`, {
          id: `s_${d.id}`, docId: d.id, dir: "out", at: toMs(m), subject: m.subject || "(no subject)",
          address: m.to || "", from: m.from || "", cc: m.cc || [],
          html: m.content || "", text: m.text || "",
          preview: stripHtml(m.text || m.content).slice(0, 160), kind: m.emailType || null,
        });
      });
      received.forEach(d => {
        const m = d.data();
        if (m.deleted) return;
        out.set(`r_${d.id}`, {
          id: `r_${d.id}`, dir: "in", at: toMs(m), subject: m.subject || "(no subject)",
          address: m.fromEmail || "", preview: stripHtml(m.preview || m.bodyText).slice(0, 160),
        });
      });
      setSnapshot({
        requestKey,
        mails: [...out.values()].sort((a, b) => (b.at || 0) - (a.at || 0)),
      });
    });
    return () => { cancelled = true; };
  }, [requestKey, worldId, contactId, key]);

  const loading = Boolean(requestKey) && snapshot.requestKey !== requestKey;
  const mails = snapshot.requestKey === requestKey ? snapshot.mails : [];
  return { mails, loading };
}
